import { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { HomeContext, type HomePageChannel, type StoreTeamSwitchAggregation } from '../state/home-context'
import { useApiGetTeams } from '../../../core/api/team/useApiGetTeams'
import { useApiGetWeatherToday } from '../../../core/api/weather/useApiGetWeatherToday'
import { HOMEPAGE_EVENTS } from 'core/constants/homepage-analytics'
import { useHomepageAnalytics } from '../analytics/use-homepage-analytics'
import ChannelSelect from './channel-select'
import StoreTeamSwitch from './store-team-switch'
import TeamSelect from './team-select'
import Weather from './weather'
import StoreDirectorMessageEdition from './store-director-message/store-director-message-edition'

const HomeHeader = () => {
  const { t } = useTranslation()
  const trackHomepageEvent = useHomepageAnalytics()
  const { channel, aggregation, storeId, teamId, userFirstName, setChannel, setAggregation, setTeamId } =
    useContext(HomeContext)
  const isTeamView = aggregation === 'TEAM'
  const { data: teams, isLoading: isLoadingTeams } = useApiGetTeams({ storeId }, { enabled: !!storeId })
  const { data: weather, isLoading: isLoadingWeather } = useApiGetWeatherToday({ storeId }, { enabled: !!storeId })
  const hasWeatherData = weather?.weatherCode != null

  const handleAggregationChange = useCallback(
    (value: StoreTeamSwitchAggregation) => {
      trackHomepageEvent(HOMEPAGE_EVENTS.AGGREGATION_CHANGED, { aggregation: value })
      setAggregation(value)
    },
    [setAggregation, trackHomepageEvent]
  )

  const handleTeamChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      if (!event.target.value) return
      const newTeamId = Number.parseInt(event.target.value)
      trackHomepageEvent(HOMEPAGE_EVENTS.TEAM_CHANGED, { team_id: newTeamId })
      setTeamId(newTeamId)
    },
    [setTeamId, trackHomepageEvent]
  )

  const handleChannelChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newChannel = event.target.value as HomePageChannel
      trackHomepageEvent(HOMEPAGE_EVENTS.CHANNEL_CHANGED, { channel: newChannel })
      setChannel(newChannel)
    },
    [setChannel, trackHomepageEvent]
  )

  return (
    <div className="mg-homepage-header">
      <div data-testid="home-greeting" className="mg-homepage-header-title vp-title-m">
        {t('general.homepage.hello', { firstName: userFirstName })}
      </div>
      {!isLoadingWeather && hasWeatherData && (
        <Weather
          weatherCode={weather?.weatherCode}
          temperatureMin={weather?.temperatureMin}
          temperatureMax={weather?.temperatureMax}
        />
      )}
      <div className="mg-homepage-header-actions">
        <div className="mg-homepage-aggregation">
          <StoreTeamSwitch value={aggregation} onSelect={handleAggregationChange} />
          {isTeamView && (
            <TeamSelect
              selectedTeamId={teamId ? `${teamId}` : undefined}
              teams={teams}
              isLoading={isLoadingTeams}
              onChange={handleTeamChange}
            />
          )}
        </div>
        <ChannelSelect value={channel} onChange={handleChannelChange} />
        <StoreDirectorMessageEdition />
      </div>
    </div>
  )
}

export default HomeHeader
